import { api } from "./api.service";
export type PaymentPriceDetails = {
  originalAmount: number;
  cardFeeAmount: number;
  cardTotalAmount: number;
  boletoFeeAmount: number;
  boletoTotalAmount: number;
};

export async function getPriceDetails(): Promise<PaymentPriceDetails> {
  const { data } = await api.get("/payments/price");

  const originalRaw = data?.pricing?.originalAmount ?? data?.value ?? 0;
  const cardFeeRaw = data?.pricing?.card?.feeAmount ?? 0;
  const cardTotalRaw = data?.pricing?.card?.totalAmount ?? originalRaw;
  const boletoFeeRaw = data?.pricing?.boleto?.feeAmount ?? 0;
  const boletoTotalRaw = data?.pricing?.boleto?.totalAmount ?? originalRaw;

  const toNumber = (v: any) => (typeof v === "string" ? Number(v) : Number(v ?? 0));

  return {
    originalAmount: toNumber(originalRaw),
    cardFeeAmount: toNumber(cardFeeRaw),
    cardTotalAmount: toNumber(cardTotalRaw),
    boletoFeeAmount: toNumber(boletoFeeRaw),
    boletoTotalAmount: toNumber(boletoTotalRaw),
  };
}

export async function getPrice(): Promise<number> {
  const details = await getPriceDetails();
  return details.originalAmount;
}

export type PagarPorPixInput = {
  scheduleId: string;
  startTime?: string;
  contractAccepted?: boolean;
};

export type PixPendingPayment = {
  paymentId: string;
  appointmentId?: string | null;
  scheduleId?: string | null;
  startTime?: string | null;
  expiresAt: string; // ISO
  txid?: string | null;
  pixCopiaCola: string;
};

function extractPixCopiaCola(data: any): string {
  if (typeof data === "string") return data;

  const val =
    data?.pixCopiaCola ??
    data?.pixCopieCola ??
    data?.pixcopiecola ??
    data?.pixCopiaECola ??
    data?.pix ??
    data?.payload ??
    data?.code;

  return val == null ? "" : String(val);
}

function normalizePixResponse(data: any): PixPendingPayment {
  return {
    paymentId: String(data?.paymentId ?? ""),
    appointmentId: data?.appointmentId ?? null,
    scheduleId: data?.scheduleId ?? null,
    startTime: data?.startTime ?? null,
    expiresAt: String(data?.expiresAt ?? ""),
    txid: data?.txid ?? null,
    pixCopiaCola: extractPixCopiaCola(data),
  };
}

export async function pagarPorPix(input: PagarPorPixInput): Promise<PixPendingPayment> {
  const { data } = await api.post("/payments/pix", {
    scheduleId: input.scheduleId,
    startTime: input.startTime,
    contractAccepted: true,
  });

  return normalizePixResponse(data);
}

export async function getPendingPix(): Promise<PixPendingPayment | null> {
  const { data } = await api.get("/payments/pix/pending");
  if (!data) return null;
  return normalizePixResponse(data);
}

// --- BOLETO ---

export type SolicitarBoletoInput = {
  scheduleId: string;
  startTime?: string;
  contractAccepted?: boolean;
};

export type BoletoResponse = {
  hostedVoucherUrl: string;
  expiresAt?: string | null;
  originalAmount?: number;
  feeAmount?: number;
  totalAmount?: number;
};

function normalizeBoletoResponse(data: any): BoletoResponse {
  const hostedVoucherUrl =
    data?.hostedVoucherUrl ??
    data?.hosted_voucher_url ??
    data?.url ??
    "";

  return {
    hostedVoucherUrl: String(hostedVoucherUrl),
    expiresAt: data?.expiresAt ?? data?.expires_at ?? null,
    originalAmount:
      data?.originalAmount !== undefined ? Number(data.originalAmount) : undefined,
    feeAmount: data?.feeAmount !== undefined ? Number(data.feeAmount) : undefined,
    totalAmount: data?.totalAmount !== undefined ? Number(data.totalAmount) : undefined,
  };
}

export async function solicitarBoleto(
  input: SolicitarBoletoInput,
): Promise<BoletoResponse> {
  const { data } = await api.post("/payments/boleto", {
    scheduleId: input.scheduleId,
    startTime: input.startTime,
    contractAccepted: true,
  });

  return normalizeBoletoResponse(data);
}
